import { useMemo } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../utils/api";
import { ApiResponse, ApiError } from "../types/api";
import { Snapshot } from "../types/snapshot";

const snapshotsKey = (projectId: string | undefined) => ["snapshots", projectId];

export const useSnapshots = (projectId: string | undefined, enabled = true) => {
  return useQuery<ApiResponse<Snapshot[]>, ApiError>({
    queryKey: snapshotsKey(projectId),
    queryFn: () => api.request<ApiResponse<Snapshot[]>>(`/api/projects/${projectId}/snapshots`),
    enabled: !!projectId && enabled,
    staleTime: 0,
  });
};

/** Snapshots for several projects at once, keyed by project id. */
export const useSnapshotsForProjects = (
  projectIds: (number | string)[]
): { byProject: Record<string, Snapshot[]>; isPending: boolean } => {
  const keys = useMemo(() => projectIds.map((id) => String(id)), [projectIds]);
  return useQueries({
    queries: keys.map((projectId) => ({
      queryKey: snapshotsKey(projectId),
      queryFn: () => api.request<ApiResponse<Snapshot[]>>(`/api/projects/${projectId}/snapshots`),
      staleTime: 0,
    })),
    combine: (results) => {
      const byProject: Record<string, Snapshot[]> = {};
      results.forEach((q, i) => {
        if (q.data?.data) byProject[keys[i]] = q.data.data;
      });
      return { byProject, isPending: results.some((q) => q.isPending) };
    },
  });
};

interface CreateSnapshotPayload {
  description?: string;
  image_data: string;
  mitigation_strategy_id?: string | number | null;
}

export const useCreateSnapshot = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<Snapshot>, ApiError, CreateSnapshotPayload>({
    mutationFn: (payload) =>
      api.request<ApiResponse<Snapshot>>(`/api/projects/${projectId}/snapshots`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: snapshotsKey(projectId) });
    },
  });
};

export const useDeleteSnapshot = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (snapshotId) =>
      api.request(`/api/projects/${projectId}/snapshots/${snapshotId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: snapshotsKey(projectId) });
    },
  });
};
